import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Grid,
  Chip,
  Avatar,
  Skeleton,
  useTheme,
  alpha
} from '@mui/material';
import {
  Person as PersonIcon,
  Event as EventIcon,
  Payment as PaymentIcon,
  Group as GroupIcon,
  BookmarkBorder as BookingIcon,
  Assignment as AssignmentIcon,
  TrendingUp as TrendingUpIcon,
  AccessTime as TimeIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import MUIStatCard from './ui/MUIStatCard';

const ReceptionistDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  
  const [stats, setStats] = useState({
    totalMembers: 0,
    todayBookings: 0,
    pendingPayments: 0,
    activeMembers: 0,
    dailyRevenue: 0
  });
  
  const [recentActivities, setRecentActivities] = useState([]);
  const [pendingRegistrations, setPendingRegistrations] = useState([]);
  const [todayBookings, setTodayBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch users for member statistics
      const usersResponse = await api.get('/users');
      const users = usersResponse.data.data || [];
      const activeMembers = users.filter(u => u.role === 'member' && u.registrationStatus === 'active').length;
      const totalMembers = users.filter(u => u.role === 'member').length;
      
      // Fetch today's bookings from staff-safe receptionist endpoint
      const now = new Date();
      const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

      const bookingsResponse = await api.get('/receptionist/bookings', {
        params: {
          startDate: startOfDay.toISOString(),
          endDate: endOfDay.toISOString(),
          limit: 100
        }
      });
      const todayBookingsData = bookingsResponse.data.data || [];
      
      // Fetch pending registrations
      const pendingUsers = users.filter(u => u.registrationStatus === 'pending_verification');
      
      // Calculate daily revenue (mock calculation)
      const dailyRevenue = todayBookingsData.length * 25; // Average booking fee

      setStats({
        totalMembers,
        todayBookings: todayBookingsData.length,
        pendingPayments: Math.floor(totalMembers * 0.15), // Mock pending payments
        activeMembers,
        dailyRevenue
      });

      setTodayBookings(todayBookingsData.slice(0, 5));
      setPendingRegistrations(pendingUsers.slice(0, 5));
      
      // Set recent activities
      setRecentActivities([
        { id: 1, type: 'booking', message: 'New facility booking created', time: '10 minutes ago', icon: <BookingIcon />, color: 'primary' },
        { id: 2, type: 'payment', message: 'Payment processed for member', time: '25 minutes ago', icon: <PaymentIcon />, color: 'success' },
        { id: 3, type: 'registration', message: 'New member registration', time: '1 hour ago', icon: <PersonIcon />, color: 'info' },
        { id: 4, type: 'class', message: 'Class booking confirmed', time: '2 hours ago', icon: <EventIcon />, color: 'secondary' }
      ]);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      const status = error?.response?.status;
      const url = error?.config?.url;
      const msg = error?.response?.data?.error || error.message || 'Unknown error';
      setError(`Failed to load dashboard data (${status || 'n/a'} ${url || ''}): ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    {
      title: 'Create Booking',
      description: 'Book facilities for members',
      icon: <BookingIcon sx={{ fontSize: 28 }} />,
      action: () => navigate('/receptionist/bookings/create'),
      color: theme.palette.primary.main,
      bgColor: alpha(theme.palette.primary.main, 0.1)
    },
    {
      title: 'Member Registration',
      description: 'Register new members',
      icon: <PersonIcon sx={{ fontSize: 28 }} />,
      action: () => navigate('/receptionist/registration'),
      color: theme.palette.secondary.main,
      bgColor: alpha(theme.palette.secondary.main, 0.1)
    },
    {
      title: 'Process Payment',
      description: 'Handle member payments',
      icon: <PaymentIcon sx={{ fontSize: 28 }} />,
      action: () => navigate('/receptionist/payments'),
      color: theme.palette.success.main,
      bgColor: alpha(theme.palette.success.main, 0.1)
    },
    {
      title: 'View Members',
      description: 'Browse member directory',
      icon: <GroupIcon sx={{ fontSize: 28 }} />,
      action: () => navigate('/receptionist/members'),
      color: theme.palette.info.main,
      bgColor: alpha(theme.palette.info.main, 0.1)
    }
  ];


  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'success';
      case 'cancelled': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  if (error) {
    return (
      <Container maxWidth="xl">
        <Paper 
          elevation={1} 
          sx={{ 
            p: 4, 
            mt: 4, 
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <Box sx={{ fontSize: '3rem', mb: 2 }}>⚠️</Box>
          <Typography variant="h5" color="error" sx={{ mb: 1 }}>
            Something went wrong
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {error}
          </Typography>
          <Button 
            variant="contained" 
            onClick={fetchDashboardData}
          >
            Try Again
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      {/* Welcome Section */}
      <Box sx={{ 
        mb: 5, 
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
        borderRadius: 3,
        p: 4,
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar
            sx={{
              width: 64,
              height: 64,
              mr: 3,
              bgcolor: theme.palette.primary.main,
              fontSize: '1.5rem'
            }}
          >
            <AssignmentIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, color: 'primary.main' }}>
              Receptionist Dashboard
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
              Welcome back, {user?.firstName}! Manage bookings and assist members today.
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
          <Chip 
            icon={<TrendingUpIcon />}
            label={`$${stats.dailyRevenue} revenue today`}
            variant="outlined"
            color="success"
            size="small"
          />
          <Chip 
            icon={<TimeIcon />}
            label={`${stats.todayBookings} bookings today`}
            color="primary"
            size="small"
          />
          <Chip 
            label={`${stats.activeMembers} active members`}
            color="info"
            size="small"
          />
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 5, width: '100%' }}>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <MUIStatCard
            title="Total Members"
            value={stats.totalMembers}
            icon={<PersonIcon />}
            loading={loading}
            onClick={() => navigate('/receptionist/members')}
            sx={{ height: '100%' }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <MUIStatCard
            title="Today's Bookings"
            value={stats.todayBookings}
            icon={<BookingIcon />}
            loading={loading}
            sx={{ height: '100%' }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <MUIStatCard
            title="Pending Payments"
            value={stats.pendingPayments}
            icon={<PaymentIcon />}
            loading={loading}
            onClick={() => navigate('/receptionist/payments')}
            sx={{ height: '100%' }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <MUIStatCard
            title="Active Members"
            value={stats.activeMembers}
            icon={<CheckIcon />}
            loading={loading}
            sx={{ height: '100%' }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <MUIStatCard
            title="Daily Revenue"
            value={`$${stats.dailyRevenue}`}
            icon={<TrendingUpIcon />}
            trend="+8% vs yesterday"
            trendDirection="up"
            loading={loading}
            sx={{ height: '100%' }}
          />
        </Grid>
      </Grid>

      <Grid container spacing={4} sx={{ width: '100%' }}>
        {/* Quick Actions */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 3,
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              height: '100%'
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'text.primary' }}>
              Quick Actions
            </Typography>
            <Grid container spacing={2} sx={{ width: '100%' }}>
              {quickActions.map((action, index) => (
                <Grid size={{ xs: 6 }} key={index}>
                  <Paper
                    elevation={0}
                    onClick={action.action}
                    sx={{ 
                      p: 3,
                      textAlign: 'center', 
                      cursor: 'pointer',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      border: `1px solid ${alpha(action.color, 0.2)}`,
                      borderRadius: 2,
                      bgcolor: action.bgColor,
                      minHeight: 140,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: `0 8px 25px ${alpha(action.color, 0.15)}`,
                        borderColor: action.color,
                        bgcolor: alpha(action.color, 0.05)
                      }
                    }}
                  >
                    <Box 
                      sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 56,
                        height: 56,
                        borderRadius: '50%',
                        bgcolor: action.bgColor,
                        color: action.color,
                        mx: 'auto',
                        mb: 2
                      }}
                    >
                      {action.icon}
                    </Box>
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        fontWeight: 600,
                        mb: 0.5,
                        color: 'text.primary'
                      }}
                    >
                      {action.title}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ fontSize: '0.75rem', lineHeight: 1.4 }}
                    >
                      {action.description}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        {/* Today's Bookings */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 3,
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              height: '100%'
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Today's Bookings
              </Typography>
              <Button 
                variant="contained" 
                size="small"
                onClick={() => navigate('/receptionist/bookings')}
                sx={{ borderRadius: 2 }}
              >
                View All
              </Button>
            </Box>

            {loading ? (
              <Box>
                {[1, 2, 3].map(i => (
                  <Box key={i} sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    py: 2,
                    borderBottom: i < 3 ? '1px solid' : 'none',
                    borderColor: 'divider'
                  }}>
                    <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton width="70%" height={18} sx={{ mb: 0.5 }} />
                      <Skeleton width="50%" height={14} />
                    </Box>
                    <Skeleton width={60} height={24} sx={{ borderRadius: 12 }} />
                  </Box>
                ))}
              </Box>
            ) : todayBookings.length > 0 ? (
              <Box>
                {todayBookings.map((booking, index) => (
                  <Box 
                    key={booking._id} 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      py: 2,
                      borderBottom: index < todayBookings.length - 1 ? '1px solid' : 'none',
                      borderColor: 'divider',
                      cursor: 'pointer',
                      borderRadius: 1,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                        transform: 'translateX(4px)'
                      }
                    }}
                  >
                    <Avatar
                      sx={{ 
                        mr: 2,
                        width: 40,
                        height: 40,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main
                      }}
                    >
                      <BookingIcon sx={{ fontSize: 20 }} />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography 
                        variant="subtitle2" 
                        sx={{ 
                          fontWeight: 600,
                          mb: 0.5,
                          color: 'text.primary'
                        }}
                      >
                        {booking.facility?.name || 'Facility'}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ fontSize: '0.75rem' }}
                      >
                        {booking.user?.firstName} {booking.user?.lastName}
                      </Typography>
                    </Box>
                    <Chip 
                      label={booking.status} 
                      color={getStatusColor(booking.status)}
                      size="small"
                      sx={{ 
                        fontWeight: 500,
                        textTransform: 'capitalize',
                        borderRadius: 12
                      }}
                    />
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    mx: 'auto',
                    mb: 2
                  }}
                >
                  <BookingIcon sx={{ fontSize: 32 }} />
                </Avatar>
                <Typography 
                  variant="h6"
                  color="text.secondary" 
                  sx={{ mb: 1, fontWeight: 500 }}
                >
                  No bookings today
                </Typography>
                <Typography 
                  variant="body2"
                  color="text.secondary" 
                  sx={{ mb: 3 }}
                >
                  Help members create their first booking!
                </Typography>
                <Button 
                  variant="contained" 
                  onClick={() => navigate('/receptionist/bookings/create')}
                  sx={{ borderRadius: 2 }}
                >
                  Create Booking
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Pending Registrations & Recent Activities */}
      <Grid container spacing={4} sx={{ mt: 2, width: '100%' }}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 3,
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              height: '100%'
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Pending Registrations
              </Typography>
              <Button 
                variant="text" 
                size="small"
                onClick={() => navigate('/admin/verification')}
                sx={{ fontWeight: 500 }}
              >
                View All
              </Button>
            </Box>

            {loading ? (
              <Box>
                {[1, 2, 3].map(i => (
                  <Box key={i} sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    py: 2,
                    borderBottom: i < 3 ? '1px solid' : 'none',
                    borderColor: 'divider'
                  }}>
                    <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton width="70%" height={18} sx={{ mb: 0.5 }} />
                      <Skeleton width="50%" height={14} />
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : pendingRegistrations.length > 0 ? (
              <Box>
                {pendingRegistrations.map((user, index) => (
                  <Box 
                    key={user._id} 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      py: 2,
                      borderBottom: index < pendingRegistrations.length - 1 ? '1px solid' : 'none',
                      borderColor: 'divider'
                    }}
                  >
                    <Avatar
                      sx={{ 
                        mr: 2,
                        width: 40,
                        height: 40,
                        bgcolor: alpha(theme.palette.warning.main, 0.1),
                        color: theme.palette.warning.main
                      }}
                    >
                      {user.firstName?.charAt(0)}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography 
                        variant="subtitle2" 
                        sx={{ 
                          fontWeight: 600,
                          mb: 0.5,
                          color: 'text.primary'
                        }}
                      >
                        {user.firstName} {user.lastName}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ fontSize: '0.75rem' }}
                      >
                        {user.userType} • {user.email}
                      </Typography>
                    </Box>
                    <Chip 
                      label="Pending" 
                      color="warning"
                      size="small"
                      sx={{ 
                        fontWeight: 500,
                        borderRadius: 12
                      }}
                    />
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  No pending registrations
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, lg: 6 }}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 3,
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              height: '100%'
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 3 }}>
              Recent Activities
            </Typography>

            {loading ? (
              <Box>
                {[1, 2, 3, 4].map(i => (
                  <Box key={i} sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    py: 2,
                    borderBottom: i < 4 ? '1px solid' : 'none',
                    borderColor: 'divider'
                  }}>
                    <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton width="80%" height={18} sx={{ mb: 0.5 }} />
                      <Skeleton width="40%" height={14} />
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box>
                {recentActivities.map((activity, index) => (
                  <Box 
                    key={activity.id} 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      py: 2,
                      borderBottom: index < recentActivities.length - 1 ? '1px solid' : 'none',
                      borderColor: 'divider',
                      borderRadius: 1,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.04)
                      }
                    }}
                  >
                    <Avatar
                      sx={{ 
                        mr: 2,
                        width: 40,
                        height: 40,
                        bgcolor: alpha(theme.palette[activity.color]?.main || theme.palette.primary.main, 0.1),
                        color: theme.palette[activity.color]?.main || theme.palette.primary.main
                      }}
                    >
                      {activity.icon}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography 
                        variant="subtitle2" 
                        sx={{ 
                          fontWeight: 600,
                          mb: 0.5,
                          color: 'text.primary'
                        }}
                      >
                        {activity.message}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ fontSize: '0.75rem' }}
                      >
                        {activity.time}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ReceptionistDashboard;
